import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { BreweryModalContext } from "./BreweryModalContext";
import analyticsService, {
  ANALYTICS_TYPES,
} from "@common/services/analyticsService";
import { CLEANING_TYPE } from "@common/constants/sessionConstants";

export const BreweryModalProvider = ({ children }) => {
  // Modal visibility & payload states
  const [brewState, setBrewState] = useState({
    isOpen: false,
    selectedDevice: "",
    selectedRecipe: "",
  });
  const [wifiState, setWifiState] = useState({ isOpen: false });
  const [cleaningState, setCleaningState] = useState({
    isOpen: false,
    deviceUUID: undefined,
    cleaningType: null,
  });
  const [deviceState, setDeviceState] = useState({
    isOpen: false,
    selectedSerialNumber: 0,
  });
  const [stopSessionState, setStopSessionState] = useState({
    isOpen: false,
    selectedSession: 0,
  });
  const [resetSessionState, setResetSessionState] = useState({
    isOpen: false,
    selectedSession: 0,
  });
  const [addDeviceState, setAddDeviceState] = useState({ isOpen: false });
  const [unregisterState, setUnregisterState] = useState({
    isOpen: false,
    deviceUUID: null,
  });
  const [acidSupportState, setAcidSupportState] = useState({ isOpen: false });
  const [editFermentationState, setEditFermentationState] = useState({
    isOpen: false,
    fermentationDevice: null,
  });

  // Action handlers
  const openBrewModal = useCallback((deviceUUID, recipe = "") => {
    setBrewState({
      isOpen: true,
      selectedDevice: deviceUUID,
      selectedRecipe: recipe,
    });
  }, []);

  const closeBrewModal = useCallback(() => {
    setBrewState({
      isOpen: false,
      selectedDevice: "",
      selectedRecipe: "",
    });
  }, []);

  const openWifiModal = useCallback((device) => {
    if (device) {
      analyticsService.track(ANALYTICS_TYPES.CONNECT_DEVICE, {
        DeviceID: device.uuid,
        DeviceOnline: device.online,
      });
    }
    setWifiState({ isOpen: true });
  }, []);

  const closeWifiModal = useCallback(() => {
    setWifiState({ isOpen: false });
  }, []);

  const toggleCleaningModal = useCallback((deviceUUID) => {
    setCleaningState((prev) => ({
      isOpen: !prev.isOpen,
      deviceUUID,
      cleaningType: CLEANING_TYPE.NORMAL,
    }));
  }, []);

  const toggleAcidCleaningModal = useCallback((deviceUUID) => {
    setCleaningState((prev) => ({
      isOpen: !prev.isOpen,
      deviceUUID,
      cleaningType: CLEANING_TYPE.ACID,
    }));
  }, []);

  const closeCleaningModal = useCallback(() => {
    setCleaningState({
      isOpen: false,
      deviceUUID: undefined,
      cleaningType: null,
    });
  }, []);

  const openDeviceModal = useCallback((serialNumber) => {
    setDeviceState({
      isOpen: true,
      selectedSerialNumber: serialNumber,
    });
  }, []);

  const closeDeviceModal = useCallback(() => {
    setDeviceState({
      isOpen: false,
      selectedSerialNumber: 0,
    });
  }, []);

  const openStopSessionModal = useCallback((sessionID) => {
    setStopSessionState({
      isOpen: true,
      selectedSession: sessionID,
    });
  }, []);

  const closeStopSessionModal = useCallback(() => {
    setStopSessionState({
      isOpen: false,
      selectedSession: 0,
    });
  }, []);

  const openResetSessionModal = useCallback((sessionID) => {
    setResetSessionState({
      isOpen: true,
      selectedSession: sessionID,
    });
  }, []);

  const closeResetSessionModal = useCallback(() => {
    setResetSessionState({
      isOpen: false,
      selectedSession: 0,
    });
  }, []);

  const openAddDeviceModal = useCallback(() => {
    setAddDeviceState({ isOpen: true });
  }, []);

  const closeAddDeviceModal = useCallback(() => {
    setAddDeviceState({ isOpen: false });
  }, []);

  const openUnregisterDeviceModal = useCallback((deviceUUID) => {
    setUnregisterState({
      isOpen: true,
      deviceUUID,
    });
  }, []);

  const closeUnregisterDeviceModal = useCallback(() => {
    setUnregisterState({
      isOpen: false,
      deviceUUID: null,
    });
  }, []);

  const openAcidSupportModal = useCallback(() => {
    setAcidSupportState({ isOpen: true });
  }, []);

  const closeAcidSupportModal = useCallback(() => {
    setAcidSupportState({ isOpen: false });
  }, []);

  const openEditFermentationModal = useCallback((device) => {
    setEditFermentationState({
      isOpen: true,
      fermentationDevice: device,
    });
  }, []);

  const closeEditFermentationModal = useCallback(() => {
    setEditFermentationState({
      isOpen: false,
      fermentationDevice: null,
    });
  }, []);

  const value = useMemo(
    () => ({
      modals: {
        brew: brewState,
        wifi: wifiState,
        cleaning: cleaningState,
        device: deviceState,
        stopSession: stopSessionState,
        resetSession: resetSessionState,
        addDevice: addDeviceState,
        unregister: unregisterState,
        acidSupport: acidSupportState,
        editFermentation: editFermentationState,
      },
      actions: {
        openBrewModal,
        closeBrewModal,
        openWifiModal,
        closeWifiModal,
        toggleCleaningModal,
        toggleAcidCleaningModal,
        closeCleaningModal,
        openDeviceModal,
        closeDeviceModal,
        openStopSessionModal,
        closeStopSessionModal,
        openResetSessionModal,
        closeResetSessionModal,
        openAddDeviceModal,
        closeAddDeviceModal,
        openUnregisterDeviceModal,
        closeUnregisterDeviceModal,
        openAcidSupportModal,
        closeAcidSupportModal,
        openEditFermentationModal,
        closeEditFermentationModal,
        // Legacy aliases to ease migration
        showModal: openBrewModal,
        showWifiModal: openWifiModal,
        showDeviceModal: openDeviceModal,
        showCleaningModal: toggleCleaningModal,
        showStopSessionModal: openStopSessionModal,
        showResetSessionModal: openResetSessionModal,
        showAddDeviceModal: openAddDeviceModal,
        showUnregisterDeviceModal: openUnregisterDeviceModal,
        showAcidSupportModal: openAcidSupportModal,
        showEditFermentationModal: openEditFermentationModal,
      },
    }),
    [
      brewState,
      wifiState,
      cleaningState,
      deviceState,
      stopSessionState,
      resetSessionState,
      addDeviceState,
      unregisterState,
      acidSupportState,
      editFermentationState,
      openBrewModal,
      closeBrewModal,
      openWifiModal,
      closeWifiModal,
      toggleCleaningModal,
      toggleAcidCleaningModal,
      closeCleaningModal,
      openDeviceModal,
      closeDeviceModal,
      openStopSessionModal,
      closeStopSessionModal,
      openResetSessionModal,
      closeResetSessionModal,
      openAddDeviceModal,
      closeAddDeviceModal,
      openUnregisterDeviceModal,
      closeUnregisterDeviceModal,
      openAcidSupportModal,
      closeAcidSupportModal,
      openEditFermentationModal,
      closeEditFermentationModal,
    ],
  );

  return (
    <BreweryModalContext.Provider value={value}>
      {children}
    </BreweryModalContext.Provider>
  );
};

BreweryModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default BreweryModalProvider;
